import { useState, useEffect, useCallback, useRef } from 'react';
import { bookingsService } from '../services/api';
import { toast } from 'react-toastify';
import { 
  networkMonitor, 
  handleApiError, 
  formatErrorForUI, 
  withRetry,
  ERROR_TYPES,
  isOnline
} from '../utils/errorHandler';

// Export media query hooks
export { 
  useMediaQuery, 
  useIsMobile, 
  useIsTablet, 
  useIsDesktop,
  useDeviceType 
} from './useMediaQuery';

/**
 * Custom hook for monitoring network status
 * @returns {Object} - Network status and utilities
 */
export const useNetworkStatus = () => {
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const unsubscribe = networkMonitor.subscribe((status) => {
      setOnline(status);
    });

    return unsubscribe;
  }, []);

  return {
    isOnline: online,
    checkConnection: () => navigator.onLine
  };
};

/**
 * Custom hook for API calls with improved error handling
 * @param {Function} apiCall - The API call function
 * @param {Object} options - Configuration options
 * @returns {Object} - API state and utilities
 */
export const useApiCall = (apiCall, options = {}) => {
  const {
    autoRetry = false,
    maxRetries = 3,
    showErrorToast = true,
    onSuccess = null,
    onError = null,
    errorContext = 'API Request'
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const abortControllerRef = useRef(null);

  const execute = useCallback(async (...args) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Check network before proceeding
    if (!isOnline()) {
      const networkError = {
        errorType: ERROR_TYPES.NETWORK,
        errorMessage: 'No internet connection. Please check your network.',
        isNetworkError: true
      };
      setError(networkError);
      if (showErrorToast) {
        toast.error('No internet connection. Please check your network.', {
          autoClose: false,
          icon: false
        });
      }
      if (onError) onError(networkError);
      return { success: false, error: networkError };
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      
      if (autoRetry) {
        result = await withRetry(
          () => apiCall(...args),
          {
            maxRetries,
            onRetry: (attempt) => setRetryCount(attempt)
          }
        );
      } else {
        result = await apiCall(...args);
      }

      setData(result);
      setRetryCount(0);
      if (onSuccess) onSuccess(result);
      return { success: true, data: result };
    } catch (err) {
      const errorDetails = handleApiError(err, {
        showToast: showErrorToast,
        context: errorContext
      });
      
      setError(errorDetails);
      if (onError) onError(errorDetails);
      return { success: false, error: errorDetails };
    } finally {
      setLoading(false);
    }
  }, [apiCall, autoRetry, maxRetries, showErrorToast, onSuccess, onError, errorContext]);

  const retry = useCallback(() => {
    execute();
  }, [execute]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setRetryCount(0);
  }, []);

  // Cancel request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    errorForUI: error ? formatErrorForUI(error) : null,
    retryCount,
    execute,
    retry,
    reset,
    isNetworkError: error?.errorType === ERROR_TYPES.NETWORK,
    isTimeoutError: error?.errorType === ERROR_TYPES.TIMEOUT,
    canRetry: error?.canRetry || false
  };
};

/**
 * Custom hook for managing booking data with improved error handling
 */
export const useBookings = (initialFilters = {}) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const { isOnline: networkOnline } = useNetworkStatus();

  const loadBookings = async () => {
    // Check network first
    if (!networkOnline) {
      toast.error('No internet connection. Please check your network and try again.', {
        autoClose: false,
        icon: false
      });
      setError({ message: 'No internet connection', isNetworkError: true });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Support paginated responses: if API returns pagination.totalPages, fetch all pages
      const perPage = Number(filters.limit) || 500;
      let pageNum = Number(filters.page) || 1;
      let accumulated = [];
      let totalPages = 1;

      console.log(`Starting bookings fetch: page=${pageNum}, limit=${perPage}, filters=`, filters);

      // Fetch first page (or the page specified in filters)
      const firstResp = await bookingsService.getAll({ ...filters, page: pageNum, limit: perPage });

      const normalizeResp = (apiResp) => {
        if (!apiResp) return { items: [], pagination: null };
        let items = [];
        let pagination = null;
        if (apiResp.success && apiResp.data) {
          if (Array.isArray(apiResp.data)) items = apiResp.data;
          else if (Array.isArray(apiResp.data.bookings)) items = apiResp.data.bookings;
          pagination = apiResp.pagination || apiResp.data.pagination || null;
        } else if (Array.isArray(apiResp)) {
          items = apiResp;
        } else if (Array.isArray(apiResp.data?.bookings)) {
          items = apiResp.data.bookings;
          pagination = apiResp.data.pagination || null;
        }
        return { items, pagination };
      };

      const first = normalizeResp(firstResp);
      accumulated.push(...first.items);
      console.log(`Page 1: ${first.items.length} items, pagination:`, first.pagination);
      
      if (first.pagination && first.pagination.totalPages) {
        totalPages = Number(first.pagination.totalPages);
      } else if (first.items.length < perPage) {
        totalPages = pageNum; // likely last page
      } else {
        totalPages = pageNum; // assume single page unless pagination provided
      }

      console.log(`Total pages to fetch: ${totalPages}, accumulated so far: ${accumulated.length}`);

      // If there are more pages, fetch them
      const safetyLimit = 50;
      for (let p = pageNum + 1; p <= totalPages && p <= safetyLimit; p++) {
        console.log(`Fetching page ${p}...`);
        try {
          const resp = await bookingsService.getAll({ ...filters, page: p, limit: perPage });
          const { items, pagination } = normalizeResp(resp);
          accumulated.push(...items);
          console.log(`Page ${p}: ${items.length} items, total accumulated: ${accumulated.length}`);
          if (pagination && pagination.totalPages) {
            totalPages = Number(pagination.totalPages);
          } else if (items.length < perPage) {
            console.log(`Reached last page at page ${p}`);
            totalPages = p; // reached last page
            break;
          }
        } catch (e) {
          console.error(`Error fetching bookings page ${p}:`, e);
          break;
        }
      }

      console.log(`Finished pagination: Total bookings loaded: ${accumulated.length}`);
      setBookings(accumulated);
    } catch (err) {
      console.error('Error loading bookings:', err);
      
      // Handle different error types
      if (err.isNetworkError || err.errorType === ERROR_TYPES.NETWORK) {
        toast.error('Connection Error: Unable to load bookings. Please check your internet connection.', {
          autoClose: false,
          icon: false
        });
        setError({ message: 'Connection error', isNetworkError: true });
      } else if (err.isTimeoutError || err.errorType === ERROR_TYPES.TIMEOUT) {
        toast.error('Request Timeout: The server is taking too long to respond. Please try again.', {
          autoClose: 5000,
          icon: false
        });
        setError({ message: 'Request timeout', isTimeoutError: true });
      } else {
        toast.error(err.errorMessage || 'Failed to load bookings');
        setError({ message: err.errorMessage || err.message || 'Failed to load bookings' });
      }
      
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const updateBooking = async (bookingId, updateData) => {
    if (!networkOnline) {
      toast.error('No internet connection. Cannot update booking.');
      throw new Error('No internet connection');
    }

    try {
      const response = await bookingsService.update(bookingId, updateData);
      
      if (response.success || response.data) {
        // Update the booking in the local state
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.id === bookingId 
              ? { ...booking, ...updateData }
              : booking
          )
        );
        toast.success('Booking updated successfully');
        return response;
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      console.error('Error updating booking:', err);
      
      if (err.isNetworkError || err.isTimeoutError) {
        toast.error(err.errorMessage || 'Connection error. Please try again.');
      } else {
        toast.error('Failed to update booking');
      }
      throw err;
    }
  };

  const confirmBooking = async (bookingId) => {
    if (!networkOnline) {
      toast.error('No internet connection. Cannot confirm booking.');
      return;
    }

    try {
      await bookingsService.confirm(bookingId);
      await loadBookings(); // Refresh the list
      toast.success('Booking confirmed successfully');
    } catch (err) {
      console.error('Error confirming booking:', err);
      if (err.isNetworkError || err.isTimeoutError) {
        toast.error(err.errorMessage || 'Connection error. Please try again.');
      } else {
        toast.error('Failed to confirm booking');
      }
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!networkOnline) {
      toast.error('No internet connection. Cannot cancel booking.');
      return;
    }

    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        await bookingsService.cancel(bookingId);
        await loadBookings(); // Refresh the list
        toast.success('Booking cancelled successfully');
      } catch (err) {
        console.error('Error cancelling booking:', err);
        if (err.isNetworkError || err.isTimeoutError) {
          toast.error(err.errorMessage || 'Connection error. Please try again.');
        } else {
          toast.error('Failed to cancel booking');
        }
      }
    }
  };

  const checkInBooking = async (bookingId) => {
    if (!networkOnline) {
      toast.error('No internet connection. Cannot check in guest.');
      return;
    }

    if (window.confirm('Are you sure you want to check in this guest?')) {
      try {
        await bookingsService.checkIn(bookingId);
        await loadBookings(); // Refresh the list
        toast.success('Guest checked in successfully');
      } catch (err) {
        console.error('Error checking in guest:', err);
        if (err.isNetworkError || err.isTimeoutError) {
          toast.error(err.errorMessage || 'Connection error. Please try again.');
        } else {
          toast.error('Failed to check in guest');
        }
      }
    }
  };

  const checkOutBooking = async (bookingId) => {
    if (!networkOnline) {
      toast.error('No internet connection. Cannot check out guest.');
      return;
    }

    if (window.confirm('Are you sure you want to check out this guest?')) {
      try {
        const response = await bookingsService.checkOut(bookingId);
        await loadBookings(); // Refresh the list
        
        // Handle payment warnings from backend
        if (response && response.warning) {
          toast.warning(response.warning, { autoClose: 8000 });
          toast.success('Guest checked out successfully');
        } else {
          toast.success('Guest checked out successfully');
        }
      } catch (err) {
        console.error('Error checking out guest:', err);
        if (err.isNetworkError || err.isTimeoutError) {
          toast.error(err.errorMessage || 'Connection error. Please try again.');
        } else {
          // Handle payment validation errors from backend
          const errorMessage = err.response?.data?.error || err.error || err.message || 'Failed to check out guest';
          toast.error(errorMessage, { autoClose: 10000 });
        }
      }
    }
  };

  useEffect(() => {
    loadBookings();
  }, [filters, networkOnline]);

  return {
    bookings,
    loading,
    error,
    filters,
    setFilters,
    loadBookings,
    updateBooking,
    confirmBooking,
    cancelBooking,
    checkInBooking,
    checkOutBooking,
    isNetworkError: error?.isNetworkError,
    isTimeoutError: error?.isTimeoutError
  };
};

/**
 * Custom hook for managing modal state
 */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const toggleModal = () => setIsOpen(prev => !prev);

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal
  };
};

/**
 * Custom hook for managing form state
 */
export const useForm = (initialState = {}, onSubmit) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouchedState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setValue = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const setError = (name, error) => {
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const setTouched = (name) => {
    setTouchedState(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(values);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setValues(initialState);
    setErrors({});
    setTouchedState({});
    setIsSubmitting(false);
  };

  return {
    values,
    errors,
    touched,
    isSubmitting,
    setValue,
    setError,
    setTouched,
    handleSubmit,
    reset
  };
};

/**
 * Custom hook for pagination
 */
export const usePagination = (data = [], itemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const nextPage = () => {
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    goToPage(currentPage - 1);
  };

  return {
    currentPage,
    totalPages,
    currentData,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
    startIndex: startIndex + 1,
    endIndex: Math.min(endIndex, data.length),
    totalItems: data.length
  };
};

/**
 * Custom hook for search and filtering
 */
export const useSearch = (data = [], searchFields = []) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const filteredData = data.filter(item => {
    // Search filter
    const matchesSearch = !searchTerm || 
      searchFields.some(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item);
        return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    
    // Status filter
    const matchesStatus = statusFilter === 'ALL' || item.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
  };

  return {
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filteredData,
    clearFilters,
    hasFilters: searchTerm || statusFilter !== 'ALL'
  };
};

/**
 * Custom hook for local storage
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

/**
 * Custom hook for authentication
 */
export const useAuth = () => {
  const [user, setUser] = useLocalStorage('user', null);
  const [token, setToken] = useLocalStorage('token', null);

  const isAuthenticated = Boolean(token && user);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.reload();
  };

  const hasRole = (requiredRoles = []) => {
    if (!user) return false;
    const userRole = user.role || user.Role;
    return requiredRoles.includes(userRole);
  };

  return {
    user,
    token,
    isAuthenticated,
    login,
    logout,
    hasRole
  };
};
